import React, { useEffect, useState } from "react";
import { MdClose } from "react-icons/md";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
function StoreProfileForm({
  register,
  handleSubmit,
  errors,
  onSubmit,
  watch,
  setTabs,
  formData,
  setFormdate,
  setValue,
  categories,
  setCategories,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [categorieslist, setCategorieslist] = useState([]);

  // ✅ Fetch categories from API
  const categorylistdata = async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/store_category/get-store-categories`,
        { filters: {}, page: 1, limit: 10 },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      const getdata = res.data?.result?.storeCategories?.map((cat) => ({
        id: cat.id,
        title: cat.title,
      }));
      setCategorieslist(getdata || []);
    } catch (error) {
      console.error("Category Fetch Error:", error);
    }
  };

  useEffect(() => {
    categorylistdata();
  }, []);

  const filteredCategories = categorieslist.filter(
    (cat) =>
      cat.title.toLowerCase().includes(categoryInput.toLowerCase()) &&
      !categories.some((selected) => selected.id === cat.id)
  );

  // ✅ Category Add
  const addCategory = (cat) => {
    if (cat && !categories.some((c) => c.id === cat.id)) {
      const updatedCategories = [...categories, cat];
      setCategories(updatedCategories);
      setCategoryInput("");
      setShowDropdown(false);
      setValue(
        "categories",
        updatedCategories.map((c) => c.id),
        { shouldValidate: true }
      );
    }
  };

  // ✅ Category Remove
  const removeCategory = (cat) => {
    const updatedCategories = categories.filter((c) => c.id !== cat.id);
    setCategories(updatedCategories);
    setValue(
      "categories",
      updatedCategories.map((c) => c.id),
      { shouldValidate: true }
    );
  };

  // ✅ Manual registration for files
  useEffect(() => {
    register("cover_image", { required: "Cover image is required" });
    register("profile_image", { required: "Profile image is required" });
    register("categories", {
      validate: (value) =>
        value && value.length > 0 ? true : "Select at least one category",
    });
  }, [register]);

  const backgroundprofile = watch("cover_image");
  const profileupload = watch("profile_image");

  // ✅ Handle file upload
  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setValue(fieldName, file, { shouldValidate: true });
    }
  };

  // ✅ Form Submit
  const handleNext = () => {
    const cover = watch("cover_image");
    const profile = watch("profile_image");

    // Show alert if missing images
    if (!cover) {
      toast.error("⚠️ Please upload a cover image before proceeding.");
      return;
    }
    if (!profile) {
      toast.error("⚠️ Please upload a profile image before proceeding.");
      return;
    }

    // If both images are uploaded, validate and move to next tab
    handleSubmit(() => setTabs(4))();
  };

  return (
    <div className="max-full mx-auto px-0 sm:px-4 py-6 space-y-10">
      {/* Cover + Profile Image Upload Section */}
      <div className="relative rounded-lg py-4">
        {/* Cover Image */}
        <div className="w-full h-[100px] sm:h-[320px] rounded-lg overflow-hidden relative group">
          <input
            type="file"
            accept="image/*"
            id="cover-upload"
            className="hidden"
            onChange={(e) => handleImageChange(e, "cover_image")}
          />
          <label htmlFor="cover-upload" className="cursor-pointer block">
            <img
              src={
                backgroundprofile
                  ? URL.createObjectURL(backgroundprofile)
                  : "/image/backgroundprofile.png"
              }
              alt="Cover"
              className="w-full h-[100px] sm:h-[280px] object-cover group-hover:opacity-80 transition"
            />
          </label>
          <div className="absolute top-2 right-2 bg-white px-2 py-1 text-sm rounded shadow hidden group-hover:block">
            Select Image
          </div>
        </div>
        {errors.cover_image && (
          <p className="text-red-500 text-sm mt-1">
            {errors.cover_image.message}
          </p>
        )}

        {/* Profile Image */}
        <div className="absolute -bottom-4 left-3 sm:-bottom-7 sm:left-6">
          <div className="relative w-[70px] h-[70px] sm:w-[170px] sm:h-[170px] overflow-hidden group">
            <input
              type="file"
              accept="image/*"
              id="profile-upload"
              className="hidden"
              onChange={(e) => handleImageChange(e, "profile_image")}
            />
            <label htmlFor="profile-upload" className="cursor-pointer block">
              <img
                src={
                  profileupload
                    ? URL.createObjectURL(profileupload)
                    : "/image/profilepic.png"
                }
                alt="Profile"
                className="w-[70px] h-[70px] sm:w-full sm:h-full object-cover group-hover:opacity-80 transition"
              />
            </label>
            <div className="absolute bottom-2 right-2 bg-white text-xs px-2 py-1 rounded shadow hidden group-hover:block">
              Change
            </div>
          </div>
        </div>
        {errors.profile_image && (
          <p className="text-red-500 text-sm mt-20 sm:mt-28">
            {errors.profile_image.message}
          </p>
        )}
      </div>

      <div className="max-w-3xl space-y-6 mt-5">
        {/* Store Name */}
        <div>
          <label className="font-semibold text-sm">Store Name</label>
          <input
            type="text"
            {...register("store_name", { required: "Store name is required" })}
            placeholder="Enter Store Name"
            className="w-full border border-gray-300 rounded-md px-4 py-2 mt-1 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-700"
          />
          {errors.store_name && (
            <p className="text-sm text-red-500">{errors.store_name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="font-semibold text-sm">Description</label>
          <textarea
            {...register("store_description", {
              required: "Store description is required",
              minLength: {
                value: 10,
                message: "Description must be at least 10 characters",
              },
            })}
            placeholder="Enter Description"
            rows={4}
            maxLength={1000}
            className="w-full border border-gray-300 rounded-md px-4 py-2 mt-1 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-700"
          />
          <p className="text-xs text-right text-gray-400 mt-1">
            {watch("store_description") ? watch("store_description").length : 0}{" "}
            / 1000 Characters
          </p>
          {errors.store_description && (
            <p className="text-sm text-red-500">
              {errors.store_description.message}
            </p>
          )}
        </div>

        {/* Categories */}
        <div className="w-full relative">
          <label className="font-semibold text-sm">Categories</label>
          <div className="flex flex-wrap items-center gap-2 mt-2 border border-gray-300 rounded-md px-2 py-2 focus-within:ring-2 focus-within:ring-green-700">
            {categories.map((cat) => (
              <span
                key={cat.id}
                className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
              >
                {cat.title}
                <button
                  type="button"
                  onClick={() => removeCategory(cat)}
                  className="text-red-500 hover:text-red-700"
                >
                  <MdClose size={14} />
                </button>
              </span>
            ))}
            <input
              type="text"
              placeholder={
                categories.length === 0 ? "Type a category and press Enter" : ""
              }
              value={categoryInput}
              onChange={(e) => {
                setCategoryInput(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
              className="flex-grow min-w-[120px] border-none focus:outline-none"
            />
          </div>
          {errors.categories && (
            <p className="text-sm text-red-500 mt-1">
              {errors.categories.message}
            </p>
          )}

          {/* Dropdown */}
          {showDropdown && filteredCategories.length > 0 && (
            <ul className="absolute bg-white border border-gray-300 rounded-md w-full mt-1 max-h-40 overflow-auto shadow-lg z-10">
              {filteredCategories.map((cat) => (
                <li
                  key={cat.id}
                  className="px-4 py-2 cursor-pointer hover:bg-green-100"
                  onMouseDown={() => addCategory(cat)}
                >
                  {cat.title}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={() => setTabs(2)}
            className="px-3 flex items-center gap-10 py-2 rounded-full bg-green-800 text-white hover:bg-green-900"
          >
            <FaAngleLeft /> Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="px-3 flex items-center gap-10 py-2 rounded-full bg-green-800 text-white hover:bg-green-900"
          >
            Next <FaAngleRight />
          </button>
        </div>
      </div>
    </div>
  );
}

export default StoreProfileForm;
